/**
 * ScopeContext — 子文档渲染作用域上下文。
 *
 * 提供嵌套的作用域查找链：ScopeContext → parent ScopeContext → PaneContext。
 * 用于 ForBlockRenderer（每次迭代创建一个 ScopeProvider）和 ContentBlockRenderer
 * （已执行的动态 content 创建 ScopeProvider）中的 ::render[id]:: 解析。
 *
 * 最大嵌套深度 MAX_SCOPE_DEPTH = 5，与 ContinuationGuard 保持一致。
 */

import type {
    ContentDefinition,
    ForDefinition,
    PluginDefinition,
    PluginOutput,
} from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import { createContext, useContext, useMemo } from "react";
export {
    findContentDefinitionInScope,
    findDefinitionInScope,
    findForDefinitionInScope,
    findOutputInScope,
} from "../lib/document-render-logic";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

/* ── Constants ── */

/** 最大作用域嵌套深度 */
export const MAX_SCOPE_DEPTH = 5;

/* ── Types ── */

export interface ScopeContextValue {
    /** scope 内的 plugin definitions */
    definitions: Map<string, PluginDefinition>;
    /** scope 内的 for definitions */
    forDefinitions: Map<string, ForDefinition>;
    /** scope 内的 content definitions */
    contentDefinitions: Map<string, ContentDefinition>;
    /** scope 内所有执行单元的输出 (id → output) */
    unitOutputs: Record<string, PluginOutput>;
    /** 父 scope（形成栈） */
    parent: ScopeContextValue | null;
    /** 当前嵌套深度 */
    depth: number;
    /** For 迭代的循环变量（用于 InlineRef 的 $each 解析） */
    loopVar?: {
        name: string;
        item: unknown;
        index: number;
    };
}

/* ── Context ── */

const ScopeContext = createContext<ScopeContextValue | null>(null);

/* ── Provider ── */

export interface ScopeProviderProps {
    /** scope 内的 definitions */
    definitions: Map<string, PluginDefinition>;
    /** scope 内的 for definitions */
    forDefinitions?: Map<string, ForDefinition>;
    /** scope 内的 content definitions */
    contentDefinitions?: Map<string, ContentDefinition>;
    /** scope 内所有执行单元的输出 */
    unitOutputs: Record<string, PluginOutput>;
    /** For 迭代的循环变量 */
    loopVar?: { name: string; item: unknown; index: number };
    children: React.ReactNode;
}

/**
 * 在子树中建立一个新的作用域层。
 * 内部 ::render[id]:: 先在当前 scope 查找，再递归到 parent，最终到 PaneContext。
 */
export function ScopeProvider({
    definitions,
    forDefinitions,
    contentDefinitions,
    unitOutputs,
    loopVar,
    children,
}: ScopeProviderProps) {
    const t = useT("editor");
    const parent = useContext(ScopeContext);

    const depth = (parent?.depth ?? 0) + 1;

    const value = useMemo<ScopeContextValue>(
        () => ({
            definitions,
            forDefinitions: forDefinitions ?? new Map(),
            contentDefinitions: contentDefinitions ?? new Map(),
            unitOutputs,
            parent,
            depth,
            loopVar,
        }),
        [definitions, forDefinitions, contentDefinitions, unitOutputs, parent, depth, loopVar],
    );

    if (depth > MAX_SCOPE_DEPTH) {
        // 超过最大深度，不再嵌套渲染
        return (
            <div
                className="text-[10px] px-2 py-1 rounded theme-accent-chip"
                style={getThemeAccentStyle("warning")}
            >
                {t("scope.maxDepthExceeded", { max: MAX_SCOPE_DEPTH })}
            </div>
        );
    }

    return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

/* ── Hooks ── */

/**
 * 获取当前作用域上下文（可能为 null — 表示在顶层，没有嵌套 scope）。
 */
export function useScopeContext(): ScopeContextValue | null {
    return useContext(ScopeContext);
}

/**
 * 在作用域链上查找最近的 loopVar。
 */
export function findLoopVarInScope(
    scope: ScopeContextValue | null,
): ScopeContextValue["loopVar"] | undefined {
    let current = scope;
    while (current) {
        if (current.loopVar) return current.loopVar;
        current = current.parent;
    }
    return undefined;
}
